import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ApiService } from '@core/services/api.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { HeaderService } from '@core/services/header.service';

@Component({
  standalone: false,
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss']
})
export class OverviewComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  currentUser: any = null;
  loading = true;

  totalEmployees = 0;
  newThisMonth = 0;
  presentToday = 0;
  onLeaveToday = 0;
  absentToday = 0;
  lateToday = 0;
  lateMoreThanYesterday = 0;
  approvedLeaves = 0;
  pendingLeavesCount = 0;
  attendanceRate = 0;

  presentArc = 0;
  leaveArc = 0;

  todayAttendanceList: any[] = [];
  allEmployees: any[] = [];

  pendingLeaveRequests: any[] = [];

  weekTrend: any[] = [];

  todayDateStr = '';

  myTodayRecord: any = null;
  checkingIn = false;
  checkingOut = false;
  timerDisplay = '00:00:00';
  private timerInterval: any = null;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private headerService: HeaderService,
    private router: Router,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Dashboard');
    this.currentUser = this.authService.getCurrentUser();
    this.todayDateStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  private loadData(): void {
    this.loading = true;
    const todayStr = new Date().toISOString().split('T')[0];

    this.apiService.get('/employees').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (emps: any) => {
        this.allEmployees = emps || [];
        this.totalEmployees = this.allEmployees.length;
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        this.newThisMonth = this.allEmployees.filter((e: any) => {
          if (!e.dateOfJoining) return false;
          const d = new Date(e.dateOfJoining);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        }).length;

        this.loadTodayAttendance(todayStr);
        this.loadMyAttendance(todayStr);
      },
      error: () => { this.loading = false; }
    });

    this.apiService.get('/leaves').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (leaves: any) => {
        const allLeaves = leaves || [];
        const pendingLeaves = allLeaves.filter((l: any) => l.status === 'pending' && this.isEmployeeActiveById(l.employeeId));
        const approvedToday = allLeaves.filter((l: any) => {
          if (l.status !== 'approved') return false;
          const start = l.startDate;
          const end = l.endDate;
          const today = new Date().toISOString().split('T')[0];
          return start <= today && end >= today;
        });
        this.onLeaveToday = approvedToday.length;
        this.approvedLeaves = approvedToday.length;
        this.pendingLeavesCount = pendingLeaves.length;

        this.pendingLeaveRequests = pendingLeaves.slice(0, 5).map((l: any) => {
          const emp = this.allEmployees.find((e: any) => e.id === l.employeeId);
          const name = emp ? (emp.firstName + ' ' + emp.lastName) : 'Unknown';
          const start = new Date(l.startDate);
          const end = new Date(l.endDate);
          const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1);
          return { id: l.id, employeeName: name, leaveType: l.leaveType, days };
        });
      },
      error: () => {}
    });
  }

  private isEmployeeActiveById(id: string): boolean {
    const emp = this.allEmployees.find((e: any) => e.id === id || e.employeeId === id || e.userId === id);
    return !!emp && (emp.status || '').toLowerCase() !== 'inactive';
  }

  private loadTodayAttendance(todayStr: string): void {
    this.apiService.get('/attendance?date=' + todayStr).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (records: any) => {
        const recs = records || [];
        this.presentToday = recs.filter((r: any) => r.status === 'present' || r.checkIn).length;
        this.lateToday = recs.filter((r: any) => {
          if (!r.checkIn) return false;
          const t = this.normalizeTime(r.checkIn);
          const parts = t.split(':');
          return (+parts[0]) * 60 + (+parts[1]) > 9 * 60 + 15;
        }).length;
        this.absentToday = Math.max(0, this.totalEmployees - this.presentToday - this.onLeaveToday);
        this.attendanceRate = this.totalEmployees > 0 ? Math.round((this.presentToday / this.totalEmployees) * 100) : 0;

        const circ = 301.6;
        this.presentArc = this.totalEmployees > 0 ? (this.presentToday / this.totalEmployees) * circ : 0;
        this.leaveArc = this.totalEmployees > 0 ? (this.onLeaveToday / this.totalEmployees) * circ : 0;

        this.todayAttendanceList = recs.map((r: any) => {
          const emp = this.allEmployees.find((e: any) => e.id === r.employeeId);
          const name = emp ? (emp.firstName + ' ' + emp.lastName) : 'Employee';
          const ciStr = this.normalizeTime(r.checkIn);
          const coStr = this.normalizeTime(r.checkOut);
          let hours = '—';
          if (ciStr) {
            const ciParts = ciStr.split(':');
            const ciDate = new Date();
            ciDate.setHours(+ciParts[0], +ciParts[1], ciParts[2] ? +ciParts[2] : 0);
            let coDate: Date;
            if (coStr) {
              const coParts = coStr.split(':');
              coDate = new Date();
              coDate.setHours(+coParts[0], +coParts[1], coParts[2] ? +coParts[2] : 0);
            } else {
              coDate = new Date();
            }
            const diff = Math.max(0, Math.floor((coDate.getTime() - ciDate.getTime()) / 1000));
            const h = Math.floor(diff / 3600);
            const m = Math.floor((diff % 3600) / 60);
            hours = `${h}h ${this.pad(m)}m`;
          }
          const isLate = ciStr ? ((+ciStr.split(':')[0]) * 60 + (+ciStr.split(':')[1]) > 9 * 60 + 15) : false;
          let status = 'present';
          let statusLabel = 'Present';
          if (r.status === 'on_leave' || r.status === 'leave') {
            status = 'leave'; statusLabel = 'On Leave';
          } else if (!r.checkIn) {
            status = 'absent'; statusLabel = 'Absent';
          } else if (isLate) {
            status = 'late'; statusLabel = 'Late';
          }
          return {
            employeeName: name,
            checkIn: ciStr ? this.formatTime12(ciStr) : null,
            checkOut: coStr ? this.formatTime12(coStr) : null,
            hours, status, statusLabel
          };
        });

        this.buildWeekTrend();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private buildWeekTrend(): void {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    this.weekTrend = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      const isWknd = d.getDay() === 0 || d.getDay() === 6;
      const isPast = d <= today;
      this.weekTrend.push({
        dayName: dayNames[i],
        isWeekend: isWknd,
        percent: isWknd ? 0 : (isPast ? this.attendanceRate : 0)
      });
    }
  }

  private normalizeTime(time: any): string {
    if (!time) return '';
    if (Array.isArray(time)) {
      const h = time[0] ?? 0;
      const m = time[1] ?? 0;
      const s = time[2] ?? 0;
      return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
    }
    return String(time);
  }

  formatTime12(time: string): string {
    if (!time) return '';
    const parts = time.split(':');
    let h = +parts[0];
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${this.pad(h)}:${m} ${ampm}`;
  }

  private pad(n: number): string { return n < 10 ? '0' + n : '' + n; }

  getEmpInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = ['#334155', '#475569', '#0f766e', '#166534', '#92400e', '#7c3a5a', '#3f4a5f', '#5b6472'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  goToAttendance(): void {
    this.router.navigate(['/attendance']);
  }

  navigate(route: string): void {
    this.router.navigate([route]);
  }

  get workPercent(): number {
    if (!this.myTodayRecord?.checkIn) return 0;
    const t = this.normalizeTime(this.myTodayRecord.checkIn);
    const parts = t.split(':');
    const ci = new Date();
    ci.setHours(+parts[0], +parts[1], parts[2] ? +parts[2] : 0);
    const end = this.myTodayRecord.checkOut ? (() => {
      const co = this.normalizeTime(this.myTodayRecord.checkOut).split(':');
      const d = new Date(); d.setHours(+co[0], +co[1], co[2] ? +co[2] : 0); return d;
    })() : new Date();
    const diff = Math.max(0, (end.getTime() - ci.getTime()) / 1000);
    return Math.min(100, Math.round((diff / (8 * 3600)) * 100));
  }

  private loadMyAttendance(todayStr: string): void {
    this.apiService.get('/attendance?date=' + todayStr).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (records: any) => {
        const myRecs = (records || []).filter((r: any) => r.employeeId === this.currentUser?.id);
        this.myTodayRecord = myRecs.length > 0 ? myRecs[myRecs.length - 1] : null;
        this.startTimer();
      },
      error: () => {}
    });
  }

  private startTimer(): void {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.myTodayRecord?.checkIn && !this.myTodayRecord?.checkOut) {
      this.updateTimer();
      this.timerInterval = setInterval(() => this.updateTimer(), 1000);
    }
  }

  private updateTimer(): void {
    if (!this.myTodayRecord?.checkIn) return;
    const t = this.normalizeTime(this.myTodayRecord.checkIn);
    const parts = t.split(':');
    const ci = new Date();
    ci.setHours(+parts[0], +parts[1], parts[2] ? +parts[2] : 0);
    const diff = Math.floor((Date.now() - ci.getTime()) / 1000);
    if (diff < 0) return;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    this.timerDisplay = `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  get totalWorked(): string {
    if (!this.myTodayRecord?.checkIn || !this.myTodayRecord?.checkOut) return '00:00:00';
    const ciStr = this.normalizeTime(this.myTodayRecord.checkIn);
    const coStr = this.normalizeTime(this.myTodayRecord.checkOut);
    const ciParts = ciStr.split(':');
    const coParts = coStr.split(':');
    const ci = new Date(); ci.setHours(+ciParts[0], +ciParts[1], ciParts[2] ? +ciParts[2] : 0);
    const co = new Date(); co.setHours(+coParts[0], +coParts[1], coParts[2] ? +coParts[2] : 0);
    const diff = Math.max(0, Math.floor((co.getTime() - ci.getTime()) / 1000));
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  get isLateCheckIn(): boolean {
    if (!this.myTodayRecord?.checkIn) return false;
    const t = this.normalizeTime(this.myTodayRecord.checkIn).split(':');
    return (+t[0]) * 60 + (+t[1] || 0) > 9 * 60 + 15;
  }

  get checkInTime(): string {
    if (!this.myTodayRecord?.checkIn) return '';
    return this.formatTime12(this.normalizeTime(this.myTodayRecord.checkIn));
  }

  get checkOutTime(): string {
    if (!this.myTodayRecord?.checkOut) return '';
    return this.formatTime12(this.normalizeTime(this.myTodayRecord.checkOut));
  }

  checkIn(): void {
    this.checkingIn = true;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => this.performCheckIn(pos.coords.latitude, pos.coords.longitude),
        () => this.performCheckIn(null, null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      this.performCheckIn(null, null);
    }
  }

  private performCheckIn(lat: number | null, lng: number | null): void {
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    const payload: any = {
      employeeId: this.currentUser?.id,
      date: now.toISOString().split('T')[0],
      checkIn: time,
      status: 'present'
    };
    if (lat !== null && lng !== null) {
      payload.location = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    }
    this.apiService.post('/attendance', payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (record: any) => {
        this.myTodayRecord = record;
        this.checkingIn = false;
        this.startTimer();
        this.loadTodayAttendance(now.toISOString().split('T')[0]);
      },
      error: () => { this.checkingIn = false; }
    });
  }

  checkOut(): void {
    if (!this.myTodayRecord) return;
    this.confirmShortCheckout(this.myTodayRecord.checkIn, () => this.doCheckOut());
  }

  // Accidental-checkout guard: a check-out within 30 minutes of checking in is almost always
  // a double-click/mis-tap — confirm before recording it.
  private confirmShortCheckout(checkInTime: string | null | undefined, proceed: () => void): void {
    let mins = 999;
    const m = /^(\d{1,2}):(\d{2})/.exec(String(checkInTime || '').includes('T')
      ? String(checkInTime).split('T')[1] : String(checkInTime || ''));
    if (m) {
      const now = new Date();
      mins = (now.getHours() * 60 + now.getMinutes()) - ((+m[1]) * 60 + (+m[2]));
    }
    if (mins >= 0 && mins < 30) {
      this.confirmDialog.confirm(
        `You checked in only ${mins <= 1 ? 'a minute' : mins + ' minutes'} ago — check out anyway?`,
        { title: 'Confirm Check Out', confirmLabel: 'Check Out', danger: true },
      ).then(ok => { if (ok) proceed(); });
      return;
    }
    proceed();
  }

  private doCheckOut(): void {
    if (!this.myTodayRecord) return;
    this.checkingOut = true;
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    this.apiService.patch('/attendance/' + this.myTodayRecord.id, { checkOut: time }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (record: any) => {
        this.myTodayRecord = record;
        this.checkingOut = false;
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.loadTodayAttendance(now.toISOString().split('T')[0]);
      },
      error: () => { this.checkingOut = false; }
    });
  }

  approveLeaveReq(id: number): void {
    this.apiService.patch('/leaves/' + id, { status: 'approved' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.pendingLeaveRequests = this.pendingLeaveRequests.filter(lr => lr.id !== id);
        this.pendingLeavesCount = Math.max(0, this.pendingLeavesCount - 1);
      },
      error: () => {}
    });
  }

  rejectLeaveReq(id: number): void {
    this.apiService.patch('/leaves/' + id, { status: 'rejected' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.pendingLeaveRequests = this.pendingLeaveRequests.filter(lr => lr.id !== id);
        this.pendingLeavesCount = Math.max(0, this.pendingLeavesCount - 1);
      },
      error: () => {}
    });
  }
}
